/**
 * FMethodeManager (DWA-A 138-1)
 *
 * Responsibility:
 * - Step 6: select fMethode option from DB
 * - If option is "manual" (custom_factor=1): show manual factor input
 * - Persist fMethodeOption (via select) and fMethode (via hidden field)
 * - If not manual fK/ki: derive fK and ki where possible
 */
export default class FMethodeManager {
    /**
     * @param {jQuery} form
     * @param {Object} state
     * @param {Object} host instance of ConfigurationForm
     */
    constructor(form, state, host) {
        this.form = form;
        this.state = state;
        this.host = host;

        this.$step = $('#fMethodeStep6');
        this.$wrapper = this.$step.closest('.fMethodeWizardStep6');
        this.optionsMap = {};

        this.wasActive = false;
        this.wasManualBefore = false;
    }

    decodeHtmlEntities(str) {
        return String(str)
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>');
    }

    init() {
        if (!this.$step.length) {
            return;
        }

        this.parseOptionsMap();
        this.bindHandlers();
        this.updateVisibility();
        this.applyFromSelection(true);
    }

    parseOptionsMap() {
        const raw = this.$step.attr('data-fmethode-map');
        if (!raw) {
            this.optionsMap = {};
            return;
        }
        try {
            const json = this.decodeHtmlEntities(raw);
            this.optionsMap = JSON.parse(json) || {};
        } catch (e) {
            console.error('[FMethodeManager] Failed to parse fMethode options map', e);
            this.optionsMap = {};
        }
    }

    bindHandlers() {
        this.form.on('change', '#fMethodeStep6 select.fMethodeOption', () => {
            this.applyFromSelection();
        });

        this.form.on('input', '#fMethodeStep6 .fMethodeManualInput', () => {
            this.applyFromSelection();
        });
    }

    updateVisibility() {
        const isActive = (typeof this.host.isGuideline1381Active === 'function')
            ? (this.host.isGuideline1381Active() && this.host.kiWizardManager.getDecision() !== 1 && this.host.fkWizardManager.getDecision() !== 1)
            : false;

        if (!isActive && this.wasActive) {
            this.reset();
        }

        if (isActive) {
            this.$wrapper.show();
            this.$step.show();
            if (typeof this.host.setInputsDisabled === 'function') {
                this.host.setInputsDisabled(this.$wrapper, false);
            }
        } else {
            if (typeof this.host.setInputsDisabled === 'function') {
                this.host.setInputsDisabled(this.$wrapper, true);
            }
            this.$step.hide();
            this.$wrapper.hide();
        }

        this.wasActive = isActive;
    }

    applyFromSelection(init = false) {
        if (!this.$step.length) {
            return;
        }

        const $optionSelect = this.$step.find('select.fMethodeOption');
        const $factorSelect = this.$step.find('.fMethodeManualInput');
        const $nameRow = this.$step.find('.fMethodeManualNameRow');

        const optionUid = parseInt($optionSelect.val(), 10);
        if (!optionUid) {
            this.setFMethode(0);
            $factorSelect.val('').prop('disabled', true);
            $nameRow.hide();
            this.computeDerivedValues();
            return;
        }

        const meta = this.optionsMap[String(optionUid)] || this.optionsMap[optionUid];
        const isManual = meta && meta.customFactor === 1;

        if (isManual) {
            $nameRow.show();
			$factorSelect.prop('disabled', false);
            // Reset factor if it was pre-filled from a non-manual method before
            if (!this.wasManualBefore) {
                $factorSelect.val('');
            }
            this.wasManualBefore = true;

            const manualFactor = this.getManualFactor(init);
            this.setFMethode(manualFactor !== null ? manualFactor : 0);
			// set manual input value from hidden field on initial load
			if (init) {
				$factorSelect.val(
					manualFactor.toString()
				);
			}
        } else {
            this.wasManualBefore = false;
            $nameRow.hide();
            $factorSelect.prop('disabled', true);
            const factor = meta ? parseFloat(meta.factor) : 0;
            // Pre-fill factor dropdown with value from selected method
            if (!isNaN(factor) && factor > 0) {
                // Try both "1" and "1.0" for selection matching
                $factorSelect.val(factor.toFixed(1));
                if ($factorSelect.val() === null || $factorSelect.val() === '') {
                    $factorSelect.val(String(factor));
                }
            } else {
                $factorSelect.val('');
            }
            this.setFMethode(isNaN(factor) ? 0 : factor);
        }

        // Derive fK / ki when possible
        this.computeDerivedValues();
    }

    isValid() {
        if (!this.$step.length || !this.$step.is(':visible')) {
            return true;
        }
        const $optionSelect = this.$step.find('select.fMethodeOption');
        if (!$optionSelect.val()) {
            return false;
        }
        const factor = this.getManualFactor();
        return factor !== null && factor > 0;
    }

    getManualFactor(init = false) {
		if (init) {
			return this.$step.find('.fMethodeManualInput').data('initial');
		}

        const raw = String(this.$step.find('.fMethodeManualInput').val() || '').trim();
        if (raw === '') {
            return null;
        }
        const normalized = raw.replace(',', '.');
        const value = parseFloat(normalized);
        if (isNaN(value)) {
            return null;
        }
        return value;
    }

    setFMethode(value) {
        const $field = this.form.find('input.fMethodeValue').first();
        if (!$field.length) {
            return;
        }
        $field.val(String(value));
        $field.trigger('change');
    }

    computeDerivedValues() {
        // fK
        const fKIsManual = this.getPersistedBool('.fKIsManual');
        const fOrt = this.getPersistedNumber('input.fortValue');
        const fMethode = this.getPersistedNumber('input.fMethodeValue');

        if (!fKIsManual && fOrt !== null && fOrt > 0 && fMethode !== null && fMethode > 0) {
            const fK = Math.min(fOrt * fMethode, 1.0);
            // Round fK to 2 decimals as per Step 3 requirement
            this.setFK(Math.round(fK * 100) / 100);
        }

        // ki
        const kiIsManual = this.getPersistedBool('.kiIsManual');
        if (!kiIsManual) {
            const k = this.getPersistedNumber('input[name*="[configuration][customRunoffValue]"]');
            const fK = this.getPersistedNumber('input.fKValue');
            if (k !== null && k > 0 && fK !== null && fK > 0) {
                // Fixed rounding issue for ki derivation
                const val = k * fK;
                this.setKi(Math.round(val * 10000000) / 10000000);
            }
        }
    }

    setFK(value) {
        const $field = this.form.find('input.fKValue').first();
        if (!$field.length) {
            return;
        }
        $field.val(String(value));
        $field.trigger('change');
    }

    setKi(value) {
        const $field = this.form.find('input.kiValue').first();
        if (!$field.length) {
            return;
        }
        $field.val(String(value));
		this.host.updateFormData($field[0]);
    }

    getPersistedBool(selector) {
        const $field = this.form.find(selector).first();
        if (!$field.length) {
            return null;
        }
        const raw = String($field.val() || '').trim();
        if (raw === '') {
            return null;
        }
        return raw === '1' || raw === 'true';
    }

    getPersistedNumber(selector) {
        const $field = this.form.find(selector).first();
        if (!$field.length) {
            return null;
        }
        const raw = String($field.val() || '').trim().replace(',', '.');
        if (raw === '') {
            return null;
        }
        const value = parseFloat(raw);
        return isNaN(value) ? null : value;
    }

    reset() {
        // Keep selection as-is? Reset is safer when switching away from guideline.
        const $select = this.$step.find('select.fMethodeOption');
        $select.val('');
        this.applyFromSelection();
    }
}
